import React from 'react'

import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

type Props<T extends React.ElementType> = Overwrite<
  CommonProps<T>,
  {
    as?: T
    children?: React.ReactNode
    icon?: React.ReactElement<{title?: string}>
    iconAlign?: 'left' | 'right'
    size?: '80' | '90' | '100' | '200' | '300'
    appearance?: 'base' | 'minor' | 'success' | 'error' | 'warning'
    disabled?: boolean
    onClick?: (event: React.MouseEvent, href: string) => void
    href?: string
  }
>

const LinkButton = <T extends React.ElementType = 'button'>({
  className,
  children,
  as,
  href = '#',
  icon,
  iconAlign = 'left',
  size = '100',
  appearance = 'base',
  disabled = false,
  onClick,
  ...props
}: Props<T>) => {
  const Root = (as ?? 'button') as React.ElementType

  const iconOnly = !!icon && !children

  const handleClick: React.MouseEventHandler = event => {
    if (onClick) {
      onClick(event, href)
    }
  }

  return (
    <Root
      type={Root === 'button' ? 'button' : undefined}
      {...props}
      className={cn(
        'LinkButton',
        {
          [`size-${size}`]: !!size,
          [`appearance-${appearance}`]: !!appearance,
          disabled,
          iconOnly,
        },
        className
      )}
      aria-disabled={disabled}
      disabled={disabled}
      aria-label={iconOnly ? icon.props.title : undefined}
      onClick={onClick ? handleClick : undefined}
      href={href}
    >
      {icon && iconAlign === 'left' && <span className="LinkButton__icon">{icon}</span>}
      {children && <span className="LinkButton__label">{children}</span>}
      {icon && iconAlign === 'right' && <span className="LinkButton__icon">{icon}</span>}
    </Root>
  )
}

export default LinkButton
